import styled from 'styled-components';

import { ThemeProp } from '~/theme/Themes';

export const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
`;

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

// Rendered through a portal into `document.body` (see TokenInput.tsx) rather than positioned
// absolutely inside `InputWrapper`, this field commonly sits inside a scrollable ancestor (e.g.
// the output-paths list), which would otherwise clip the dropdown to its own bounds instead of
// letting it float freely below the field. Positioned via inline `style` (`position: fixed`,
// computed `top`/`left`/`width`) rather than CSS, since it must track the field's actual viewport
// position, not a static offset from a relative parent.
export const DropdownCard = styled.div`
  // Portaled to document.body, so this competes directly against other body-level chrome instead
  // of a local ancestor stacking context, SideBar and Banner both use z-index: 1000, so this must
  // clear that outright rather than tie (a tie falls back to DOM order, which isn't guaranteed).
  z-index: 1100;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
  border-radius: 0.5rem;
  box-shadow: 0 0.1875rem 0.3125rem rgba(0, 0, 0, 0.03), 0 0.625rem 1.25rem rgba(0, 0, 0, 0.05);
  padding: 0.75rem;
  max-height: 16rem;
  overflow-y: auto;
`;

interface DropdownOptionProps {
  $isActive: boolean;
}

export const DropdownOption = styled.div<DropdownOptionProps>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  background-color: ${(props: ThemeProp & DropdownOptionProps) =>
    props.$isActive ? props.theme.colors.neutrals.c100 : 'transparent'};

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }
`;

export const DropdownOptionName = styled.span`
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  font-size: 0.875rem;
  line-height: 1.375rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
`;

export const DropdownOptionHelper = styled.span`
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  font-style: italic;
  font-size: 0.75rem;
  line-height: 1rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.375rem;
`;

export const FieldLabelGroup = styled.div`
  display: flex;
  align-items: center;
`;

export const FieldLabel = styled.label`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  display: inline-block;
`;

interface EditorProps {
  $hasError: boolean;
  $isReadOnly: boolean;
}

// Wraps the editor and, outside its border, the remove-path trailing action.
export const EditorRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
`;

const getEditorBorderColor = (props: ThemeProp & EditorProps) =>
  props.$hasError ? props.theme.colors.errorRed : props.theme.colors.neutrals.c200;

// Wraps Editor and the floated adornment, and owns the single visible border/background for the
// whole field.
export const EditorBox = styled.div<EditorProps>`
  position: relative;
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  min-height: 2.375rem;
  border-radius: 0.375rem;
  border: 1px solid ${(props: ThemeProp & EditorProps) => getEditorBorderColor(props)};
  background-color: ${(props: ThemeProp & EditorProps) =>
    props.$isReadOnly ? props.theme.colors.neutrals.c100 : props.theme.colors.neutrals.white};
`;

// Floats `editorAdornment` (e.g. the "insert variable" button) inside the editor box, vertically
// centered, near its right edge, rather than as a flex sibling, since it must overlay the box
// rather than take up its own row space.
export const EditorAdornmentSlot = styled.div`
  position: absolute;
  right: 0.375rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
`;

interface EditorStyleProps extends EditorProps {
  $hasEditorAdornment?: boolean;
}

// The contentEditable token editor: literal text renders as plain text nodes, variables render as
// atomic `[data-var]` chip spans built imperatively by `renderInto` (see tokenDom.ts). Chip
// styling is expressed here as attribute-selector CSS (rather than a separate React component)
// since chips are DOM nodes created outside of React's render tree.
//
// Fills the row (`flex: 1 1 auto`) like a normal multi-line text field, wrapping long content
// within its own width. The file-extension suffix (`data-extension`) is rendered as a `::after`
// pseudo-element.
export const Editor = styled.div<EditorStyleProps>`
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.5rem ${(props: EditorStyleProps) => (props.$hasEditorAdornment ? '2.5rem' : '0.75rem')} 0.5rem 0.75rem;
  font-family: monospace;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  word-break: break-all;
  cursor: ${(props: EditorProps) => (props.$isReadOnly ? 'default' : 'text')};

  &:empty::before {
    content: attr(data-placeholder);
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  }

  &[data-extension]::after {
    content: attr(data-extension);
    color: #ababab;
    font-family: 'Fira Code', monospace;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.25rem;
  }

  &:focus-visible {
    outline: none;
  }

  [data-var] {
    position: relative;
    display: inline-flex;
    margin: 0 0.125rem;
    border-radius: 0.25rem;
    padding: 1px 0.25rem;
    background-color: ${(props: ThemeProp) => props.theme.colors.primaryLight};
    color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    cursor: pointer;
  }

  [data-var]:focus-visible {
    outline: 0.125rem solid ${(props: ThemeProp) => props.theme.colors.primary.c500};
    outline-offset: 1px;
  }

  [data-var][data-unsupported='true'] {
    background-color: ${(props: ThemeProp) => props.theme.colors.system.error.c100};
    color: ${(props: ThemeProp) => props.theme.colors.system.error.c500};
  }
`;

interface ChipTooltipProps {
  $placement: 'top' | 'bottom';
}

// Hover/focus tooltip for variable chips. Portaled to `document.body` (see TokenInput.tsx) rather
// than a plain CSS `::after` on the chip, a chip commonly sits inside the scrollable output-paths
// list, which would otherwise clip a tooltip that tries to render above it. Positioned via inline
// `style` (computed from the chip's own bounding box, flipped above/below depending on available
// space) for the same reason the autocomplete dropdown is.
export const ChipTooltip = styled.div<ChipTooltipProps>`
  position: fixed;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  word-break: normal;
  overflow-wrap: break-word;
  width: max-content;
  max-width: 16rem;
  // Sits above DropdownCard's 1100 too — both are portaled to body, and a chip's own dropdown
  // can't be open at the same time as its tooltip, but keeping a consistent ordering avoids relying
  // on DOM insertion order between the two if that ever changes.
  z-index: 1100;
  pointer-events: none;
  transform: ${(props: ChipTooltipProps) =>
    props.$placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)'};
`;

// A plain clickable icon (e.g. a remove icon) in the same row as the editor, outside its border,
// but centered against the editor row's own height specifically (`align-self: center`), not the
// label above it, and not sharing the editor/suffix's border or background.
export const TrailingActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex-shrink: 0;
  margin-left: 0.5rem;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};

  svg {
    flex-shrink: 0;
  }
`;

export const ErrorText = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  color: ${(props: ThemeProp) => props.theme.colors.errorRed};
`;
